import { useMemo } from "react";
import type {
  PerpsFormModel,
  PerpsTradingWidgetActions,
  PerpsTradingWidgetFormState,
  PerpsTradingWidgetMarket,
  PerpsTradingWidgetState,
} from "@cede/trading-ui";
import type { CexPerpsFormLogic } from "./useCexPerpsFormLogic";

const noopVoid = () => {};

/** Stub TP/SL fields the cex-perps hook doesn't expose — the composite never
 *  reads them when slots.tpSlSection is provided (which both consumers do). */
const STUB_TP_DERIVED = { price: "", gainPercent: undefined, valid: true };
const STUB_SL_DERIVED = { price: "", lossPercent: undefined, valid: true };

function toPerpsFormModel(form: CexPerpsFormLogic): PerpsFormModel {
  return {
    handleSideChange: form.handleSideChange,
    handleUnifiedSizeChange: form.handleUnifiedSizeChange,
    handlePercentageChange: form.handlePercentageChange,
    sizePercentage: form.sizePercentage,
    // CEX perps handles max sizing with its own leverage-scaled safety buffer
    // and never forwards the flag to a venue — always false here.
    isMaxSized: false,
    sizeMode: form.sizeMode,
    setSizeMode: form.setSizeMode,
    handleLeverageConfirm: form.handleLeverageConfirm,
    tpDerived: STUB_TP_DERIVED,
    slDerived: STUB_SL_DERIVED,
    handleTpPriceChange: noopVoid,
    handleTpGainPercentChange: noopVoid,
    handleSlPriceChange: noopVoid,
    handleSlLossPercentChange: noopVoid,
    hasTpSlError: false,
    handleTpSlToggle: noopVoid,
    orderDetailItems: form.orderDetailItems,
    quoteAmount: form.quoteAmount,
    isLimitOrdersDisabled: form.isLimitOrdersDisabled,
    isTpSlDisabled: true,
  };
}

export interface CexPerpsCompositeProps {
  form: PerpsFormModel;
  formState: PerpsTradingWidgetFormState;
  state: PerpsTradingWidgetState;
  market: PerpsTradingWidgetMarket;
  actions: PerpsTradingWidgetActions;
}

/**
 * Adapt {@link CexPerpsFormLogic} to the shared `<PerpsTradingWidget>` prop
 * surface. Used by both the desktop widget and the mobile drawer.
 *
 * Returns `form` (not `formModel`) so callers can spread directly onto
 * `<PerpsTradingWidget {...compositeProps} />`.
 */
export function useCexPerpsCompositeProps(form: CexPerpsFormLogic): CexPerpsCompositeProps {
  const formModel = useMemo(() => toPerpsFormModel(form), [form]);

  const formState: PerpsTradingWidgetFormState = {
    orderSide: form.perpsSide,
    orderType: form.perpsOrderType,
    marginMode: form.perpsMarginMode,
    marginModeReadOnly: form.marginModeReadOnly,
    leverage: form.leverage,
    // PerpsSizeInput shows the active mode's value: quoteAmount when in quote mode, base size otherwise.
    size: form.sizeMode === "quote" ? (form.quoteAmount ?? "") : form.size,
    limitPrice: form.price,
    showTP: false,
    showSL: false,
    reduceOnly: false,
  };

  const state: PerpsTradingWidgetState = {
    balance: null,
    currentPosition: null,
    isSubmitting: form.isCreatingOrder,
    availableBalanceLabel: form.balanceDisplay,
    balanceLoading: form.isBalanceLoading,
  };

  const market: PerpsTradingWidgetMarket = {
    symbol: form.selectedMarket ? `${form.selectedMarket.base}/${form.selectedMarket.quote}` : "",
    base: form.selectedMarket?.base ?? "---",
    quote: form.selectedMarket?.quote ?? "USD",
    maxLeverage: form.maxLeverage,
  };

  const actions: PerpsTradingWidgetActions = {
    onSubmit: () => form.handleOrderSubmit(),
    onOrderTypeChange: form.handleOrderTypeChange,
    onMarginModeChange: form.handleMarginModeChange,
    onReduceOnlyChange: noopVoid,
    onLimitPriceChange: form.setPrice,
  };

  return { form: formModel, formState, state, market, actions };
}
